import { Box, Flex, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CustomHeading, fromUnixSeconds } from 'shared-components';
import { IAuditLog, IEmail } from 'types';
import Search from '../../../atoms/search';
import ApplicantHeader from '../../../molecules/applicantHeader';
import { ApplicationState } from '../../../../store';
import { getApplyAuditLog, getApplyEmails } from '../../../../store/apply/actions';
import { useTranslation } from "react-i18next";

/**
 * Strips SAML provider prefix from a username (e.g., "GoogleSAML_user@email.com" -> "user@email.com")
 */
function stripSamlPrefix(username: string): string {
  const samlPrefixMatch = username.match(/^[A-Za-z]+SAML_(.+)$/);
  return samlPrefixMatch ? samlPrefixMatch[1] : username;
}

/**
 * Formats the actor display name from backend-sanitized audit log data.
 * The backend redacts usernames for non-privileged viewers by setting who=alias.
 * If who differs from alias, the viewer is privileged and sees both.
 */
function formatActorDisplay(log: IAuditLog): string {
  if (log.alias && log.alias !== log.who) {
    return `${log.alias} (${stripSamlPrefix(log.who)})`;
  }
  return stripSamlPrefix(log.who);
}

export default function ActivityLog() {
    const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const params = useParams();

  useEffect(() => {
    const { uni, userId: applicantId, applicationId } = params;

    dispatch(getApplyAuditLog(uni, applicantId, applicationId));
    dispatch(getApplyEmails(uni, applicantId));
  }, []);

  const { applyAuditLogs, applyEmails } = useSelector((state: ApplicationState) => state.apply);
  const [auditLogRecords, setAuditLogRecords] = useState(applyAuditLogs);
  const [emailRecords, setEmailRecords] = useState(applyEmails);
  const [auditLogSearch, setAuditLogSearch] = useState('');

  useEffect(() => {
    if (auditLogSearch === '') {
      setAuditLogRecords(applyAuditLogs);
      setEmailRecords(applyEmails);
    } else {
      setAuditLogRecords(
        applyAuditLogs.filter(log =>
          `${log.who}${log.role}${log.action}`.toLowerCase().includes(auditLogSearch.toLowerCase()),
        ),
      );
      setEmailRecords(
        applyEmails.filter(
          email =>
            `email${email.to[0]}${email.subject}${email.from}`.toLowerCase().includes(auditLogSearch.toLowerCase()) ||
            'system'.includes(auditLogSearch.toLowerCase()),
        ),
      );
    }
  }, [auditLogSearch, applyAuditLogs, applyEmails]);

  return (
    <>
      <ApplicantHeader>
        <Box mb={8}>
          <Flex mb={2} flexWrap={'wrap'} rowGap={4} columnGap={3}>
            <CustomHeading data-selector="sectionsHeader-name" level="h1" flexGrow={1} mb={0} wordBreak={'break-word'}>
              {t('common:activityLog')}
                                      </CustomHeading>
          </Flex>
        </Box>
      </ApplicantHeader>

      <Box>
        <Box mb={3} id="audit-log-search-box">
          <Search
            currentSearch={auditLogSearch}
            hasSearchSign={true}
            placeholder={t('common:searchActivity')}
            onSearch={(searchValue: string) => setAuditLogSearch(searchValue)}
          />
        </Box>

        <Box>
          <Table size={'sm'} id="audit-log-table" sx={{ tableLayout: 'fixed' }}>
            <Thead>
              <Tr>
                <Th pl={0} width={'9rem'}>
                  {t('common:time')}
                                                  </Th>
                <Th width={{ base: '6.5rem', '2xl': '7.5rem' }}>{t('common:role')}</Th>
                <Th width={'30%'}>{t('common:actor')}</Th>
                <Th pr={0}>{t('common:activity')}</Th>
              </Tr>
            </Thead>

            <Tbody>
              {[...auditLogRecords, ...emailRecords]
                .sort((a, b) => sortLogEntries(a, b))
                .map((log: IAuditLog | any, index: number) => {
                  if (log.hasOwnProperty('when')) {
                    return (
                      <Tr key={`${log.when}.${index}`}>
                        <Td pl={0}>
                          {fromUnixSeconds(log.when).toZonedDateTimeISO('UTC').toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })}
                        </Td>
                        <Td>{log.role.replace('[', '').replace(']', '')}</Td>
                        <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                          {formatActorDisplay(log)}
                        </Td>
                        <Td pr={0}>{log.action.replace(/\*/g, '')}</Td>
                      </Tr>
                    );
                  }
                  return (
                    <Tr key={`${log.lastEmailTime}.${index}`}>
                      <Td pl={0}>
                        {fromUnixSeconds(Math.floor((log?.sentActionTime ?? 0) / 1000))
                          .toZonedDateTimeISO('UTC')
                          .toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })}
                      </Td>
                      <Td>{'System'}</Td>
                      <Td wordBreak={'break-word'} whiteSpace={'normal'}>
                        {log.to[0]}
                      </Td>
                      <Td pr={0}>{getEmailRowText(log)}</Td>
                    </Tr>
                  );
                })}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </>
  );
}

function getEmailRowText(log: IEmail) {
  let message = '';
  if (log.isComplaint) {
    message = 'Complaint';
  } else if (log.isBounced) {
    message = 'Bounced';
  } else if (log.isRead) {
    message = 'Read';
  } else if (log.isDelivered) {
    message = 'Delivered';
  } else {
    message = 'Sent';
  }
  return `Email ${message}: ${log.subject}`;
}

function sortLogEntries(a: any, b: any) {
  const aTimeLog = a.hasOwnProperty('when') ? a['when'] : Math.floor((a?.['sentActionTime'] ?? 0) / 1000);
  const bTimeLog = b.hasOwnProperty('when') ? b['when'] : Math.floor((b?.['sentActionTime'] ?? 0) / 1000);

  if (aTimeLog < bTimeLog) {
    return -1;
  }
  if (aTimeLog > bTimeLog) {
    return 1;
  }
  return 0;
}
